import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import Particles from "react-tsparticles";
import { loadSlim } from "tsparticles-slim";
import { useTheme } from "../context/ThemeContext";
import { useCart } from "../context/CartContext";
import { debounce } from "lodash";
import { motion, AnimatePresence } from "framer-motion";
import { FiMoon, FiSun, FiCheckCircle, FiMic, FiSearch, FiShare2, FiStar, FiClock, FiTruck, FiTag, FiMail, FiPlay, FiShoppingCart, FiMapPin, FiChevronDown, FiMenu, FiX } from "react-icons/fi";
import { FaApple } from "react-icons/fa";
import { AiFillHeart } from "react-icons/ai";
import Chatbot from "../components/Chatbot";
import ProfileModal from "../components/ProfileModal";

// Restaurant Data
const restaurants = [
  {
    id: 1,
    name: "I Love Pizza",
    category: "Pizza, Italian",
    rating: 4.8,
    image: "/ilovepizzapic.jpg",
    estimatedTime: "20-30 min",
    popularItems: [
      { id: "m101", name: "Margherita Pizza", price: 155, img: "https://source.unsplash.com/300x200/?margherita" },
      { id: "m1", name: "French Fries (Large)", price: 95, img: "https://source.unsplash.com/300x200/?fries" },
    ],
    discount: "10% off orders above ₹300",
  },
  {
    id: 2,
    name: "Sharma Fast Food",
    category: "Street Food",
    rating: 4.5,
    image: "/2023-10-18.webp",
    estimatedTime: "15-25 min",
    popularItems: [
      { id: "d5", name: "Paneer Roll", price: 156, img: "https://source.unsplash.com/300x200/?paneer-roll" },
      { id: "d6", name: "Chow Mein", price: 120, img: "https://source.unsplash.com/300x200/?chowmein" },
    ],
    discount: "Free delivery on orders above ₹200",
  },
  {
    id: 3,
    name: "Coffee Express",
    category: "Café, Beverages",
    rating: 4.3,
    image: "/492123435_1148793163932964_7238524074888884240_n.jpeg",
    estimatedTime: "10-20 min",
    popularItems: [
      { id: "d9", name: "Cold Coffee", price: 144, img: "https://source.unsplash.com/300x200/?cold-coffee" },
      { id: "d11", name: "Chocolate Muffin", price: 114, img: "https://source.unsplash.com/300x200/?muffin" },
    ],
  },
  {
    id: 5,
    name: "Moonlight Cafe",
    category: "Café",
    rating: 4.4,
    image: "/moonlight.jpg",
    estimatedTime: "15-25 min",
    popularItems: [
      { id: "s1", name: "Strawberry Shake", price: 84, img: "/StrawberryMilkshake.webp" },
      { id: "p1", name: "Single Cheese Pizza", price: 120, img: "https://source.unsplash.com/300x200/?cheese" },
    ],
  },
];

// Offer Data
const offers = [
  {
    id: 1,
    title: "Flash Sale: 20% Off!",
    description: "Order within the next hour to save big!",
    code: "FLASH20",
    countdown: { hours: 1, minutes: 0, seconds: 0 },
  },
  {
    id: 2,
    title: "Free Delivery",
    description: "On orders above ₹250 this weekend!",
    code: "FREESHIP",
  },
  {
    id: 3,
    title: "Combo Deal",
    description: "Buy 2 pizzas, get 1 free!",
    code: "PIZZAPARTY",
  },
];

const LandingPage = () => {
  const navigate = useNavigate();
  const { darkMode, toggleDarkMode } = useTheme();
  const cartContext = useCart();
  const { cart = [], addToCart = () => {}, toggleFavourite = () => {}, favourites = [], pastOrders = [], rewardPoints = 0 } = cartContext || {};
  const [location, setLocation] = useState("Jourian, India");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchError, setSearchError] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState([]);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [toastMessage, setToastMessage] = useState(null);
  const [selectedMode, setSelectedMode] = useState(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [voiceSearchActive, setVoiceSearchActive] = useState(false);
  const [showCartPreview, setShowCartPreview] = useState(false);
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  const [profileOpen, setProfileOpen] = useState(false);
  const [showLocationDropdown, setShowLocationDropdown] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const searchInputRef = useRef(null);

  // Load user profile from localStorage
  const savedProfile = JSON.parse(localStorage.getItem("profile") || "{}");
  const userAvatar = savedProfile.avatar || "/zoomo-mascot.png";
  const userName = savedProfile.name || "Guest";

  const cartTotalItems = useMemo(() => cart.reduce((sum, item) => sum + item.quantity, 0), [cart]);
  const contextError = !cartContext;

  // Utility Functions
  const particlesInit = async (engine) => await loadSlim(engine);
  const handleImageError = (e) => {
    e.target.src = "/zoomo-mascot.png";
  };

  // Location Autocomplete
  const fetchLocationSuggestions = useCallback(
    debounce((input) => {
      const suggestions = [
        "Jourian, India",
        "Jammu, India",
        "Akhnoor, India",
        "Srinagar, India",
        "Delhi, India",
      ].filter((loc) => loc.toLowerCase().includes(input.toLowerCase()));
      setLocationSuggestions(suggestions);
    }, 300),
    []
  );

  // Navigation and Action Handlers
  const handleNavigation = useCallback((type, value) => {
    const queries = {
      mode: `mode=${encodeURIComponent(value.toLowerCase())}`,
      filter: `filter=${encodeURIComponent(value.toLowerCase())}`,
      cuisine: `cuisine=${encodeURIComponent(value.toLowerCase())}`,
      deal: `deal=${encodeURIComponent(value)}`,
      query: `query=${encodeURIComponent(value)}`,
    };
    navigate(`/restaurants?${queries[type] || ""}`);
  }, [navigate]);

  const handleSearch = useCallback(() => {
    if (!searchQuery.trim()) {
      setSearchError(true);
      setTimeout(() => setSearchError(false), 1000);
      return;
    }
    handleNavigation("query", searchQuery);
  }, [searchQuery, handleNavigation]);

  const handleRestaurantClick = useCallback((restaurantId) => {
    const restaurant = restaurants.find((r) => r.id === restaurantId);
    if (!restaurant) {
      setToastMessage("Restaurant not found.");
      return;
    }
    setRecentlyViewed((prev) => [
      restaurant,
      ...prev.filter((r) => r.name !== restaurant.name),
    ].slice(0, 3));
    navigate(`/menu/${restaurantId}?mode=delivery`, { state: { restaurant } });
  }, [navigate]);

  const handleLocationChange = (loc) => {
    setLocation(loc);
    setLocationSuggestions([]);
    setShowLocationDropdown(false);
    setToastMessage(`Location set to ${loc}`);
  };

  const copyPromoCode = (code) => {
    navigator.clipboard.writeText(code);
    setToastMessage(`Promo code ${code} copied!`);
    navigate(`/checkout?promo=${encodeURIComponent(code)}`);
  };

  const shareRestaurant = (restaurantName) => {
    const url = `${window.location.origin}/restaurants/${restaurantName.toLowerCase().replace(/ /g, "-")}`;
    const whatsappUrl = `https://wa.me/?text=Check%20out%20${encodeURIComponent(restaurantName)}%20on%20Zoomo%20Eats!%20${encodeURIComponent(url)}`;
    window.open(whatsappUrl, "_blank");
    setToastMessage(`Shared ${restaurantName}!`);
  };

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (newsletterEmail.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setToastMessage("Subscribed to newsletter!");
      setNewsletterEmail("");
    } else {
      setToastMessage("Invalid email address.");
    }
  };

  const handleAddToCart = useCallback((dish, restaurantId) => {
    const dishObj = { id: dish.id, name: dish.name, price: dish.price, img: dish.img, restaurantId };
    addToCart(dishObj, 1, {}, null);
    setToastMessage(`${dish.name} added to cart!`);
  }, [addToCart]);

  // Effects
  useEffect(() => {
    let recognition;
    if (window.SpeechRecognition || window.webkitSpeechRecognition) {
      recognition = new (window.SpeechRecognition || window.webkitSpeechRecognition)();
      recognition.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
        handleSearch();
      };
      recognition.onend = () => setVoiceSearchActive(false);
      recognition.onerror = () => {
        setToastMessage("Voice search failed. Try again.");
        setVoiceSearchActive(false);
      };
      if (voiceSearchActive) recognition.start();
    } else {
      setToastMessage("Voice search not supported.");
      setVoiceSearchActive(false);
    }
    return () => {
      if (recognition) recognition.stop();
    };
  }, [voiceSearchActive, handleSearch]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Toast Component
  const Toast = ({ message, onClose }) => {
    useEffect(() => {
      const timer = setTimeout(onClose, 3000);
      return () => clearTimeout(timer);
    }, [onClose]);

    return (
      <motion.div
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 100 }}
        className="fixed top-4 right-4 bg-[#00b86b] text-black px-4 py-3 rounded-lg z-[1000] flex items-center gap-2 text-sm font-semibold shadow-lg"
      >
        <FiCheckCircle className="w-5 h-5" />
        {message}
      </motion.div>
    );
  };

  // Render
  if (contextError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#fafafa] dark:bg-[#111]">
        <p className="text-red-600 text-lg font-semibold">Error: Cart context unavailable. Ensure CartProvider is set up.</p>
      </div>
    );
  }

  return (
    <div className={`min-h-screen flex flex-col font-uber ${darkMode ? "bg-[#111] text-white" : "bg-[#fafafa] text-black"} relative overflow-hidden transition-colors duration-300`}>
      <style jsx global>{`
        @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');
        :root {
          --primary: #00b86b;
          --primary-dark: #009e5c;
          --bg-light: #fafafa;
          --bg-dark: #111;
          --text-light: #000000;
          --text-dark: #ffffff;
          --card-bg-light: #ffffff;
          --card-bg-dark: #222;
          --border-light: #e6f0ff;
          --border-dark: #333;
        }
        .font-uber { font-family: 'Montserrat', sans-serif; }
        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: ${darkMode ? 'var(--border-dark)' : 'var(--border-light)'}; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: var(--primary); border-radius: 10px; }
        .animate-slideIn { animation: slideIn 0.5s ease-in-out; }
        .animate-fadeIn { animation: fadeIn 0.5s ease-out; }
        @keyframes slideIn {
          from { transform: translateY(20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes marquee {
          0% { transform: translateX(100vw); }
          100% { transform: translateX(-100%); }
        }
        .marquee {
          animation: marquee 15s linear infinite;
          white-space: nowrap;
          display: inline-block;
        }
        .offer-card {
          background: ${darkMode
            ? 'linear-gradient(135deg, #004d2e 0%, #007a4b 100%)'
            : 'linear-gradient(135deg, #00b86b 0%, #009e5c 100%)'};
          border-radius: 12px;
          padding: 1.5rem;
          position: relative;
          overflow: hidden;
          transition: transform 0.3s ease, box-shadow 0.3s ease;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
        }
        .offer-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0, 184, 107, 0.4);
        }
        .section-container {
          background: ${darkMode ? 'var(--card-bg-dark)' : 'var(--card-bg-light)'};
          border-radius: 16px;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }
        button:focus, input:focus { outline: none; box-shadow: 0 0 0 3px rgba(0, 184, 107, 0.3); }
        .text-black { color: ${darkMode ? 'var(--text-dark)' : 'var(--text-light)'} !important; }
        .bg-[#e6f0ff] { background-color: ${darkMode ? 'var(--border-dark)' : 'var(--border-light)'} !important; }
        .border-gray-300 { border-color: ${darkMode ? 'var(--border-dark)' : '#d1d5db'} !important; }
        .text-gray-600 { color: ${darkMode ? '#aaaaaa' : '#4b5563'} !important; }
        .floating-cart {
          position: fixed;
          bottom: 80px;
          right: 20px;
          z-index: 1000;
          background: var(--primary);
          color: var(--text-light);
          border-radius: 50%;
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
          transition: transform 0.3s ease;
        }
        .floating-cart:hover {
          transform: scale(1.1);
        }
        .floating-cart .cart-count {
          position: absolute;
          top: -10px;
          right: -10px;
          background: red;
          color: white;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: bold;
        }
        @media (max-width: 768px) {
          .hero-text { font-size: 2.5rem; }
          .hero-subtext { font-size: 1rem; }
          .grid-cols-3 { grid-template-columns: 1fr; }
          .grid-cols-4 { grid-template-columns: 1fr 1fr; }
          .card-img { height: 140px; }
        }
        @media (max-width: 640px) {
          .hero-text { font-size: 2rem; }
          .hero-subtext { font-size: 0.875rem; }
          .grid-cols-2 { grid-template-columns: 1fr; }
        }
      `}</style>
      {toastMessage && <Toast message={toastMessage} onClose={() => setToastMessage(null)} />}
      <Particles
        id="tsparticles"
        init={particlesInit}
        options={{
          fullScreen: { enable: false },
          background: { color: darkMode ? "var(--bg-dark)" : "var(--bg-light)" },
          particles: {
            number: { value: 20 },
            size: { value: 3 },
            color: { value: "var(--primary)" },
            move: { enable: true, speed: 0.3 },
            opacity: { value: 0.1 },
          },
        }}
        className="absolute top-0 left-0 w-full h-full -z-10"
      />
<motion.header
  initial={{ y: -100 }}
  animate={{ y: 0 }}
  transition={{ duration: 0.5 }}
  className={`fixed top-0 left-0 w-full py-3 px-4 sm:px-6 flex justify-between items-center z-50 ${darkMode ? "bg-[#111]/95" : "bg-[#fafafa]/95"} backdrop-blur-md transition-all duration-300 ${isScrolled ? "shadow-lg" : "shadow-none"}`}
  role="banner"
>
  <div className="flex items-center gap-3">
    <motion.img
      src="/zoomo-logo.png"
      alt="Zoomo Eats Logo"
      className="w-10 h-10"
      whileHover={{ scale: 1.1 }}
      transition={{ duration: 0.3 }}
      onError={handleImageError}
    />
    <h2 className="text-xl sm:text-2xl font-extrabold tracking-tight uppercase">
      ZOOMO <span className="text-[var(--primary)]">EATS</span>
    </h2>
  </div>
  <nav className="flex items-center gap-4" role="navigation">
    <motion.button
      onClick={toggleDarkMode}
      whileHover={{ scale: 1.1, rotate: 15 }}
      whileTap={{ scale: 0.9 }}
      className="p-2 rounded-full bg-[var(--primary)] text-[var(--text-light)] hover:bg-[var(--primary-dark)] transition-all duration-300"
      aria-label={darkMode ? "Switch to light mode" : "Switch to dark mode"}
    >
      {darkMode ? <FiSun className="w-6 h-6" /> : <FiMoon className="w-6 h-6" />}
    </motion.button>
    <motion.button
      onClick={() => setProfileOpen(true)}
      whileHover={{ scale: 1.1 }}
      whileTap={{ scale: 0.9 }}
      className="relative group"
      aria-label="Open user profile modal"
    >
      <img
        src={userAvatar}
        alt="User Avatar"
        className="w-10 h-10 rounded-full object-cover border-2 border-[var(--primary)] group-hover:scale-110 transition-transform duration-300"
        onError={handleImageError}
      />
    </motion.button>
  </nav>
</motion.header>
      {/* Coming Soon Marquee */}
      <div className="fixed top-16 w-full overflow-hidden bg-gradient-to-r from-[var(--primary)] to-[var(--primary-dark)] text-[var(--text-light)] py-3 z-40 shadow-md">
        <motion.div
          className="marquee text-sm font-semibold flex items-center gap-2"
          initial={{ x: "100vw" }}
          animate={{ x: "-100%" }}
          transition={{ duration: 15, repeat: Infinity, ease: "linear" }}
        >
          <FiTruck className="w-5 h-5" />
          COMING SOON IN JOURIAN! Order delicious meals with Zoomo Eats! 🚀
        </motion.div>
      </div>

      {/* Floating Cart Button */}
      <motion.div
        className="floating-cart"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        onClick={() => setShowCartPreview(true)}
        aria-label="View cart"
      >
        <FiShoppingCart className="w-6 h-6" />
        {cartTotalItems > 0 && (
          <span className="cart-count">{cartTotalItems}</span>
        )}
      </motion.div>

      {/* Cart Preview */}
      <AnimatePresence>
        {showCartPreview && (
          <motion.div
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 100 }}
            className={`fixed top-20 right-4 w-[min(90vw,320px)] p-6 rounded-lg z-50 ${darkMode ? "bg-[var(--card-bg-dark)]" : "bg-[var(--card-bg-light)]"} border ${darkMode ? "border-[var(--border-dark)]" : "border-[var(--border-light)]"} animate-slideIn shadow-lg`}
          >
            <h4 className="text-lg font-semibold mb-4 text-[var(--primary)]">Your Cart</h4>
            {cart.length === 0 ? (
              <p className="text-sm text-gray-500">Cart is empty.</p>
            ) : (
              <div className="space-y-3 max-h-48 overflow-y-auto custom-scrollbar">
                {cart.map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className="flex justify-between items-center text-sm"
                  >
                    <span className="truncate">{item.name}</span>
                    <span>₹{(item.price * item.quantity).toFixed(2)}</span>
                  </motion.div>
                ))}
              </div>
            )}
            <Link
              to="/cart"
              className="block w-full bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold mt-4 text-center transition-all"
              aria-label="View full cart"
              onClick={() => setShowCartPreview(false)}
            >
              View Cart
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <ProfileModal
        darkMode={darkMode}
        setToastMessage={setToastMessage}
        toggleFavouriteDish={toggleFavourite}
        favourites={favourites}
        isOpen={profileOpen}
        onClose={() => setProfileOpen(false)}
      />

      {/* Main Content */}
      <main className="pt-28 px-4 md:px-6 pb-16 max-w-7xl mx-auto" role="main">
        {/* Hero Section */}
        <section className="w-full min-h-[calc(100vh-80px)] flex flex-col items-center justify-center text-center py-12 animate-fadeIn section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-4xl flex flex-col items-center space-y-6"
          >
            <motion.img
              src="/zoomo-mascot.png"
              alt="Zoomo Eats Mascot"
              className="w-32 h-32"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              onError={handleImageError}
            />
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-3xl md:text-5xl font-bold text-[var(--primary)] hero-text"
            >
              Discover Delicious Meals
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className={`text-base md:text-lg font-medium ${darkMode ? "text-[var(--text-dark)]" : "text-[var(--text-light)]"} max-w-md hero-subtext`}
            >
              Order from top restaurants in {location}, delivered fast.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.5 }}
              className="flex flex-col sm:flex-row items-center w-full max-w-2xl gap-3 justify-center"
            >
              <div className="relative w-full max-w-md">
                <input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search restaurants, cuisines..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                  className={`w-full px-4 py-3 rounded-lg bg-[var(--border-light)] text-[var(--text-light)] placeholder:text-gray-500 border ${searchError ? "border-red-600" : "border-[var(--border-light)]"} focus:ring-2 focus:ring-[var(--primary)] text-base pl-10 transition-all duration-300 ${darkMode ? "bg-[var(--border-dark)] text-[var(--text-dark)] placeholder:text-gray-400 border-[var(--border-dark)]" : ""}`}
                  aria-label="Search for restaurants or dishes"
                />
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <motion.button
                  onClick={() => setVoiceSearchActive(true)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[var(--primary)] hover:text-[var(--primary-dark)]"
                  aria-label="Voice search"
                >
                  <FiMic className="w-5 h-5" />
                </motion.button>
              </div>
              <motion.button
                onClick={handleSearch}
                whileHover={{ scale: 1.05, boxShadow: "0 0 15px rgba(0, 184, 107, 0.4)" }}
                whileTap={{ scale: 0.95 }}
                className="bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-[var(--text-light)] px-6 py-3 rounded-lg text-base font-semibold transition-all w-full sm:w-auto shadow-md"
                aria-label="Search"
              >
                Find Food
              </motion.button>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1, duration: 0.5 }}
              className="relative w-full max-w-md"
            >
              <button
                onClick={() => setShowLocationDropdown((prev) => !prev)}
                className={`w-full px-4 py-3 rounded-lg bg-[var(--border-light)] text-[var(--text-light)] border-[var(--border-light)] focus:ring-2 focus:ring-[var(--primary)] text-base transition-all duration-300 flex items-center justify-between ${darkMode ? "bg-[var(--border-dark)] text-[var(--text-dark)] border-[var(--border-dark)]" : ""}`}
                aria-label="Select location"
              >
                <span className="flex items-center gap-2">
                  <FiMapPin className="w-5 h-5 text-[var(--primary)]" />
                  {location}
                </span>
                <FiChevronDown className={`w-5 h-5 transition-transform ${showLocationDropdown ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {showLocationDropdown && (
                  <motion.ul
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    className={`absolute w-full mt-2 rounded-lg border ${darkMode ? "bg-[var(--card-bg-dark)] border-[var(--border-dark)] text-[var(--text-dark)]" : "bg-[var(--card-bg-light)] border-[var(--border-light)] text-[var(--text-light)]"} z-20 overflow-hidden animate-slideIn shadow-lg`}
                  >
                    <li className="px-4 py-2">
                      <input
                        type="text"
                        placeholder="Enter location..."
                        onChange={(e) => fetchLocationSuggestions(e.target.value)}
                        className={`w-full px-3 py-2 rounded-lg bg-transparent border ${darkMode ? "border-[var(--border-dark)] text-[var(--text-dark)]" : "border-[var(--border-light)] text-[var(--text-light)]"} focus:ring-2 focus:ring-[var(--primary)] text-sm`}
                        aria-label="Search location"
                      />
                    </li>
                    {locationSuggestions.length > 0 ? (
                      locationSuggestions.map((loc) => (
                        <motion.li
                          key={loc}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          onClick={() => handleLocationChange(loc)}
                          className="px-4 py-2 cursor-pointer text-sm truncate hover:bg-[var(--primary)] hover:text-[var(--text-light)] transition-colors"
                          aria-label={`Select location ${loc}`}
                        >
                          {loc}
                        </motion.li>
                      ))
                    ) : (
                      <li className="px-4 py-2 text-sm text-gray-500">No locations found</li>
                    )}
                  </motion.ul>
                )}
              </AnimatePresence>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2, duration: 0.5 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {["Fast Delivery", "Top Rated", "Offers"].map((filter, i) => (
                <motion.button
                  key={filter}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.2 + i * 0.1 }}
                  onClick={() => handleNavigation("filter", filter)}
                  whileHover={{ scale: 1.05, boxShadow: "0 0 10px rgba(0, 184, 107, 0.3)" }}
                  whileTap={{ scale: 0.95 }}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 ${darkMode ? "bg-[var(--card-bg-dark)] text-[var(--text-dark)] hover:bg-[var(--border-dark)]" : "bg-[var(--border-light)] text-[var(--text-light)] hover:bg-[#d3e3ff]"} shadow-sm`}
                  aria-label={`Filter by ${filter}`}
                >
                  {filter}
                </motion.button>
              ))}
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.4, duration: 0.5 }}
              className="flex flex-wrap justify-center gap-3"
            >
              {[
                { mode: "Delivery", icon: FiTruck, label: "Delivery" },
                { mode: "Pickup", icon: FiShoppingCart, label: "Pickup" },
                { mode: "Dine-In", icon: FiTag, label: "Dine-In" },
              ].map(({ mode, icon: Icon, label }, i) => (
                <motion.button
                  key={mode}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.4 + i * 0.1 }}
                  onClick={() => {
                    setSelectedMode(mode.toLowerCase());
                    handleNavigation("mode", mode);
                  }}
                  whileHover={{ scale: 1.05, boxShadow: "0 0 10px rgba(0, 184, 107, 0.3)" }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 min-w-[120px] justify-center ${selectedMode === mode.toLowerCase() ? "bg-[var(--primary)] text-[var(--text-light)]" : darkMode ? "bg-[var(--card-bg-dark)] text-[var(--text-dark)] hover:bg-[var(--border-dark)]" : "bg-[var(--border-light)] text-[var(--text-light)] hover:bg-[#d3e3ff]"} shadow-sm`}
                  aria-label={`Select ${mode} mode`}
                >
                  <Icon className="w-5 h-5" />
                  {label}
                </motion.button>
              ))}
            </motion.div>
          </motion.div>
        </section>

        {/* Offers Section */}
        <section className="py-12 px-4 md:px-6 animate-slideIn section-container">
          <div className="max-w-6xl mx-auto">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold text-center mb-6 text-[var(--primary)]"
            >
              Exclusive Offers
            </motion.h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {offers.map((offer, i) => (
                <motion.div
                  key={offer.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2, duration: 0.5 }}
                  className="offer-card"
                >
                  <h4 className="text-lg font-semibold mb-2 text-[var(--text-light)]">{offer.title}</h4>
                  <p className="text-sm text-[var(--text-light)]/80 mb-4">{offer.description}</p>
                  {offer.countdown && (
                    <p className="text-sm font-semibold text-yellow-300 mb-4">
                      Ends in: {offer.countdown.hours}h {offer.countdown.minutes}m
                    </p>
                  )}
                  <motion.button
                    onClick={() => copyPromoCode(offer.code)}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-[var(--text-light)] text-[var(--primary)] px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                  >
                    Claim {offer.code}
                  </motion.button>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Restaurants Section */}
        <section className="py-12 px-4 md:px-6 animate-slideIn section-container">
          <div className="max-w-6xl mx-auto">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold text-center mb-6 text-[var(--primary)]"
            >
              Featured Restaurants
            </motion.h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {restaurants.map((restaurant, i) => (
                <motion.div
                  key={`restaurant-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2, duration: 0.5 }}
                  className="transition-all duration-300 bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] rounded-lg shadow-md hover:shadow-lg"
                >
                  <div className="relative">
                    <img
                      src={restaurant.image}
                      alt={restaurant.name}
                      className="w-full h-48 object-cover rounded-t-lg card-img"
                      onError={handleImageError}
                      loading="lazy"
                    />
                    {restaurant.rating >= 4.7 && (
                      <span className="absolute top-2 left-2 bg-[var(--primary)] text-[var(--text-light)] text-xs px-2 py-1 rounded-lg">
                        Top Rated
                      </span>
                    )}
                    {restaurant.discount && (
                      <span className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded-lg">
                        {restaurant.discount}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="text-lg font-semibold mb-1 text-[var(--primary)]">{restaurant.name}</h4>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"} mb-2`}>{restaurant.category} • {restaurant.estimatedTime}</p>
                    <div className="flex items-center gap-2 mb-3">
                      <FiStar className="text-yellow-500 text-sm" />
                      <span className="text-sm font-medium">{restaurant.rating}</span>
                    </div>
                    <div className="space-y-2 mb-3">
                      {restaurant.popularItems.map((item) => (
                        <div key={item.id} className="flex justify-between items-center text-sm">
                          <span className="truncate">{item.name}</span>
                          <motion.button
                            onClick={() => handleAddToCart(item, restaurant.id)}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="bg-[var(--primary)] text-[var(--text-light)] px-3 py-1 rounded-lg text-xs font-semibold"
                          >
                            ₹{item.price} | Add
                          </motion.button>
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between items-center">
                      <motion.button
                        onClick={() => handleRestaurantClick(restaurant.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                        aria-label={`View ${restaurant.name} menu`}
                      >
                        View Menu
                      </motion.button>
                      <button
                        onClick={() => shareRestaurant(restaurant.name)}
                        className="text-gray-600 hover:text-[var(--primary-dark)] text-sm font-semibold flex items-center gap-1 transition-all"
                        aria-label={`Share ${restaurant.name}`}
                      >
                        <FiShare2 />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Dishes Section */}
        <section className="py-12 px-4 md:px-6 animate-slideIn section-container">
          <div className="max-w-6xl mx-auto">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold text-center mb-6 text-[var(--primary)]"
            >
              Popular Dishes
            </motion.h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                { id: "d1", name: "Margherita Pizza", price: 299, img: "https://source.unsplash.com/300x200/?pizza", restaurant: "I Love Pizza", isPopular: true, restaurantId: "i-love-pizza-jourian" },
                { id: "d2", name: "Pav Bhaji", price: 120, img: "https://source.unsplash.com/300x200/?pav-bhaji", restaurant: "Sharma Fast Food", restaurantId: "sharma-fast-food" },
                { id: "d3", name: "Butter Chicken", price: 399, img: "https://source.unsplash.com/300x200/?butter-chicken", restaurant: "Taste of Punjab", isPopular: true, restaurantId: "taste-of-punjab" },
              ].map((dish, i) => (
                <motion.div
                  key={`dish-${i}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.2, duration: 0.5 }}
                  className="transition-all duration-300 bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] rounded-lg shadow-md hover:shadow-lg"
                >
                  <div className="relative">
                    <img
                      src={dish.img}
                      alt={dish.name}
                      className="w-full h-48 object-cover rounded-t-lg card-img"
                      onError={handleImageError}
                      loading="lazy"
                    />
                    {dish.isPopular && (
                      <span className="absolute top-2 left-2 bg-yellow-500 text-[var(--text-light)] text-xs px-2 py-1 rounded-lg">
                        Popular
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <h4 className="text-lg font-semibold mb-1 text-[var(--primary)]">{dish.name}</h4>
                    <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"} mb-2`}>{dish.restaurant}</p>
                    <p className="text-base font-semibold mb-3 text-[var(--primary)]">₹{dish.price}</p>
                    <motion.button
                      onClick={() => handleAddToCart(dish, dish.restaurantId)}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                      aria-label={`Add ${dish.name} to cart`}
                    >
                      Add to Cart
                    </motion.button>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Recently Viewed Section */}
        <section className="py-12 px-4 md:px-6 animate-slideIn section-container">
          <div className="max-w-6xl mx-auto">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold text-center mb-6 text-[var(--primary)]"
            >
              Recently Viewed
            </motion.h3>
            {recentlyViewed.length === 0 ? (
              <motion.p
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                className={`text-base ${darkMode ? "text-[var(--text-dark)]" : "text-[var(--text-light)]"} font-medium text-center`}
              >
                No recently viewed restaurants.
              </motion.p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                {recentlyViewed.map((restaurant, i) => (
                  <motion.div
                    key={`recent-${i}`}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.2, duration: 0.5 }}
                    className="transition-all duration-300 bg-[var(--card-bg-light)] dark:bg-[var(--card-bg-dark)] rounded-lg shadow-md hover:shadow-lg"
                  >
                    <img
                      src={restaurant.image}
                      alt={restaurant.name}
                      className="w-full h-48 object-cover rounded-t-lg card-img"
                      onError={handleImageError}
                      loading="lazy"
                    />
                    <div className="p-4">
                      <h4 className="text-lg font-semibold mb-1 text-[var(--primary)]">{restaurant.name}</h4>
                      <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"} mb-2`}>{restaurant.category}</p>
                      <div className="flex items-center gap-2">
                        <FiStar className="text-yellow-500 text-sm" />
                        <span className="text-sm font-medium">{restaurant.rating}</span>
                      </div>
                      <motion.button
                        onClick={() => handleRestaurantClick(restaurant.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="mt-3 bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-[var(--text-light)] px-4 py-2 rounded-lg text-sm font-semibold transition-all"
                        aria-label={`View ${restaurant.name}`}
                      >
                        Revisit
                      </motion.button>
                    </div>
                    </motion.div>

                ))}
              </div>
            )}
          </div>
        </section>

        {/* Refer a Friend Section */}
        <section className="py-12 px-4 md:px-6 animate-slideIn section-container">
          <div className="max-w-6xl mx-auto text-center">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold mb-4 text-[var(--primary)]"
            >
              Invite Friends, Earn Rewards
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className={`text-base ${darkMode ? "text-[var(--text-dark)]" : "text-[var(--text-light)]"} font-medium mb-6 max-w-md mx-auto`}
            >
              Share Zoomo Eats and get ₹100 off when your friends order!
            </motion.p>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="flex flex-col sm:flex-row items-center justify-center gap-3"
            >
              <input
                type="text"
                value="https://zoomo-eats.com/refer"
                readOnly
                className={`px-4 py-2 rounded-lg bg-[var(--border-light)] text-[var(--text-light)] border-[var(--border-light)] focus:ring-2 focus:ring-[var(--primary)] text-base transition-all duration-300 w-full sm:w-64 ${darkMode ? "bg-[var(--border-dark)] text-[var(--text-dark)] border-[var(--border-dark)]" : ""}`}
                aria-label="Referral link"
              />
              <motion.button
                onClick={() => {
                  navigator.clipboard.writeText("https://zoomo-eats.com/refer");
                  setToastMessage("Referral link copied!");
                  navigate("/refer");
                }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-[var(--text-light)] px-6 py-2 rounded-lg text-base font-semibold transition-all flex items-center gap-2"
                aria-label="Copy referral link"
              >
                Share Link <FiShare2 />
              </motion.button>
            </motion.div>
          </div>
        </section>

        {/* Get the App Section */}
        <section className="py-12 px-4 md:px-6 animate-slideIn section-container">
          <div className="max-w-6xl mx-auto text-center">
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="text-2xl font-bold mb-4 text-[var(--primary)]"
            >
              Get the Zoomo Eats App
            </motion.h3>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className={`text-base ${darkMode ? "text-[var(--text-dark)]" : "text-[var(--text-light)]"} font-medium mb-6 max-w-md mx-auto`}
            >
              Order on the go with exclusive app-only deals!
            </motion.p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <motion.a
                href="https://play.google.com/store"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.5 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 rounded-lg flex items-center justify-center gap-2 transition-all text-base font-semibold ${darkMode ? "bg-[var(--card-bg-dark)] text-[var(--text-dark)] hover:bg-[var(--border-dark)]" : "bg-[var(--border-light)] text-[var(--text-light)] hover:bg-[#d3e3ff]"} shadow-sm`}
                aria-label="Download from Google Play"
              >
                <FiPlay className="w-5 h-5" />
                Google Play
              </motion.a>
              <motion.a
                href="https://www.apple.com/app-store"
                target="_blank"
                rel="noopener noreferrer"
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6, duration: 0.5 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-6 py-3 rounded-lg flex items-center justify-center gap-2 transition-all text-base font-semibold ${darkMode ? "bg-[var(--card-bg-dark)] text-[var(--text-dark)] hover:bg-[var(--border-dark)]" : "bg-[var(--border-light)] text-[var(--text-light)] hover:bg-[#d3e3ff]"} shadow-sm`}
                aria-label="Download from App Store"
              >
                <FaApple className="w-5 h-5" />
                App Store
              </motion.a>
            </div>
          </div>
        </section>

        {/* Footer */}
        <motion.footer
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className={`py-12 px-4 md:px-6 ${darkMode ? "bg-[var(--bg-dark)] text-[var(--text-dark)]" : "bg-[var(--bg-light)] text-[var(--text-light)]"} animate-slideIn`}
          role="contentinfo"
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-8 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-[var(--primary)]">About Zoomo Eats</h4>
                <p className="text-base font-medium">
                  Zoomo Eats delivers your favorite meals from top restaurants in {location} with speed and ease.
                </p>
                <div className="flex gap-4 mt-4">
                  {["facebook", "twitter", "instagram"].map((platform, i) => (
                    <motion.a
                      key={platform}
                      href={`https://${platform}.com`}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: i * 0.1, duration: 0.3 }}
                      whileHover={{ scale: 1.2 }}
                      className="text-[var(--primary)] hover:text-[var(--primary-dark)] text-lg"
                      aria-label={`Visit our ${platform} page`}
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path
                          d={
                            platform === "facebook"
                              ? "M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H7v-3h3V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.76 9-9.95z"
                              : platform === "twitter"
                              ? "M22.46 6c-.77.35-1.6.58-2.46.69a4.3 4.3 0 001.88-2.38c-.83.5-1.75.85-2.72 1.05a4.28 4.28 0 00-7.28 3.9A12.15 12.15 0 013.15 4.78a4.27 4.27 0 001.32 5.71c-.69-.02-1.34-.21-1.9-.5v.05a4.28 4.28 0 003.43 4.19c-.63.17-1.29.26-1.97.26-.48 0-.95-.05-1.41-.15a4.28 4.28 0 003.99 2.97A8.58 8.58 0 012 18.62a12.08 12.08 0 006.55 1.92c7.86 0 12.15-6.51 12.15-12.15 0-.18 0-.37-.01-.55A8.7 8.7 0 0022.46 6z"
                              : "M12 2.04c-5.5 0-9.96 4.46-9.96 9.96 0 4.4 3.58 8.04 8.12 8.94v-6.32h-2.44v-2.62h2.44v-1.99c0-2.41 1.47-3.72 3.61-3.72 1.03 0 1.91.08 2.17.11v2.51h-1.49c-1.17 0-1.39.56-1.39 1.37v1.79h2.78l-.36 2.62h-2.42v6.32c4.54-.9 8.12-4.54 8.12-8.94 0-5.5-4.46-9.96-9.96-9.96z"
                          }
                        />
                      </svg>
                    </motion.a>
                  ))}
                </div>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2, duration: 0.5 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-[var(--primary)]">Quick Links</h4>
                <ul className="text-base font-medium space-y-2">
                  <li>
                    <Link
                      to="/faq"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors"
                      aria-label="View FAQ"
                    >
                      FAQ
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/contact"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors"
                      aria-label="Contact us"
                    >
                      Contact
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/terms"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors"
                      aria-label="View Terms of Service"
                    >
                      Terms
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/privacy"
                      className="hover:text-[var(--primary)] focus:outline-none focus:ring-2 focus:ring-[var(--primary)] transition-colors"
                      aria-label="View Privacy Policy"
                    >
                      Privacy
                    </Link>
                  </li>
                </ul>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <h4 className="text-lg font-semibold mb-4 text-[var(--primary)]">Stay Connected</h4>
                <p className="text-base font-medium mb-4">Join our newsletter for exclusive deals!</p>
                <form onSubmit={handleNewsletterSubmit} className="flex flex-col gap-3">
                  <input
                    type="email"
                    placeholder="Enter email"
                    value={newsletterEmail}
                    onChange={(e) => setNewsletterEmail(e.target.value)}
                    className={`w-full px-4 py-2 rounded-lg bg-[var(--border-light)] text-[var(--text-light)] border-[var(--border-light)] focus:ring-2 focus:ring-[var(--primary)] text-base transition-all duration-300 ${darkMode ? "bg-[var(--border-dark)] text-[var(--text-dark)] border-[var(--border-dark)]" : ""}`}
                    aria-label="Newsletter email"
                  />
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="bg-[var(--primary)] hover:bg-[var(--primary-dark)] text-[var(--text-light)] px-4 py-2 rounded-lg text-base font-semibold transition-all flex items-center justify-center gap-2"
                    aria-label="Subscribe to newsletter"
                  >
                    <FiMail className="w-5 h-5" /> Subscribe
                  </motion.button>
                </form>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6, duration: 0.5 }}
              className="text-center text-base font-medium"
            >
              <p className="flex items-center justify-center gap-1 flex-wrap">
                © {new Date().getFullYear()} <span className="font-semibold text-[var(--primary)]">Zoomo Eats</span> — Designed with 
                <span className="text-[var(--primary)] text-xl">
                  <AiFillHeart />
                </span> 
                by Shiv
              </p>
            </motion.div>
          </div>
        </motion.footer>
      </main>
      <Chatbot />
    </div>
  );
};

export default LandingPage;